import React from 'react';
import { PlayIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, SparklesIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen13_RecordingComplete({
  onNext
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-428">
      <div className="mb-6" data-id="element-429">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-430">
          Meeting Recording Complete
        </h2>
        <p className="text-slate-400" data-id="element-431">AI finished analyzing the PTP meeting.</p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-6" data-id="element-432">
        {/* Meeting Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4" data-id="element-433">
          <div className="grid grid-cols-2 gap-4" data-id="element-434">
            <div data-id="element-435">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-436">
                Meeting Type
              </p>
              <p className="text-white font-medium text-sm" data-id="element-437">
                PTP Safety Briefing
              </p>
            </div>
            <div data-id="element-438">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-439">
                Duration
              </p>
              <p className="text-white font-medium text-sm" data-id="element-440">04:36</p>
            </div>
            <div data-id="element-441">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-442">
                Date
              </p>
              <p className="text-white font-medium text-sm" data-id="element-443">June 12, 2026</p>
            </div>
            <div data-id="element-444">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-445">
                Location
              </p>
              <p className="text-white font-medium text-sm" data-id="element-446">
                Level 2 Structural Frame
              </p>
            </div>
          </div>
        </div>

        {/* Participants Detected */}
        <section data-id="element-447">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-448">
            Participants Detected
          </h3>
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-449">
            {['Foreman', 'Crane Operator', 'Rigger', 'Ironworker', 'Laborer'].map((role, i) => <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-450">
                {role}
              </span>)}
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-451">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-452">
              Participation Quality
            </h4>
            <ul className="space-y-2 text-sm" data-id="element-453">
              <li className="flex justify-between" data-id="element-454">
                <span className="text-white" data-id="element-455">Foreman</span>
                <span className="text-green-400" data-id="element-456">high participation</span>
              </li>
              <li className="flex justify-between" data-id="element-457">
                <span className="text-white" data-id="element-458">Crane Operator</span>
                <span className="text-amber-400" data-id="element-459">medium participation</span>
              </li>
              <li className="flex justify-between" data-id="element-460">
                <span className="text-white" data-id="element-461">Rigger</span>
                <span className="text-amber-400" data-id="element-462">medium participation</span>
              </li>
              <li className="flex justify-between" data-id="element-463">
                <span className="text-white" data-id="element-464">Ironworker</span>
                <span className="text-red-400" data-id="element-465">low participation</span>
              </li>
              <li className="flex justify-between" data-id="element-466">
                <span className="text-white" data-id="element-467">Laborer</span>
                <span className="text-red-400" data-id="element-468">very low participation</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Topics Analysis */}
        <section className="space-y-3" data-id="element-469">
          <div className="bg-green-950/20 border border-green-900/30 rounded-xl p-4" data-id="element-470">
            <div className="flex items-center gap-2 mb-3" data-id="element-471">
              <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-472" />
              <h4 className="text-xs font-bold text-green-400 uppercase tracking-wider" data-id="element-473">
                Topics Confirmed as Covered
              </h4>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-474">
              {['Job steps', 'Crane setup and outriggers', 'Rigging inspection', 'Lift path', 'Exclusion zone', 'Signal person', 'Beam positioning'].map((topic, i) => <span key={i} className="bg-green-500/10 text-green-300 border border-green-500/20 px-2 py-1 rounded text-xs" data-id="element-475">
                  {topic}
                </span>)}
            </div>
          </div>

          <div className="bg-amber-950/20 border border-amber-900/30 rounded-xl p-4" data-id="element-476">
            <div className="flex items-center gap-2 mb-3" data-id="element-477">
              <AlertTriangleIcon className="w-4 h-4 text-amber-500" data-id="element-478" />
              <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider" data-id="element-479">
                Topics Partially Covered
              </h4>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-480">
              {['Dropped objects', 'Working at height'].map((topic, i) => <span key={i} className="bg-amber-500/10 text-amber-300 border border-amber-500/20 px-2 py-1 rounded text-xs" data-id="element-481">
                  {topic}
                </span>)}
            </div>
          </div>

          <div className="bg-red-950/20 border border-red-900/30 rounded-xl p-4" data-id="element-482">
            <div className="flex items-center gap-2 mb-3" data-id="element-483">
              <XCircleIcon className="w-4 h-4 text-red-500" data-id="element-484" />
              <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider" data-id="element-485">
                Topics Missing or Weak
              </h4>
            </div>
            <ul className="space-y-2 text-sm" data-id="element-486">
              {['Wind conditions', 'Emergency stop procedure', 'What to do if load becomes unstable', 'Full PPE confirmation from all crew members'].map((topic, i) => <li key={i} className="flex items-start gap-2 text-red-200" data-id="element-487">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" data-id="element-488" />
                  {topic}
                </li>)}
            </ul>
          </div>
        </section>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-489">
        <PrimaryButton onClick={onNext} icon={<SparklesIcon className="w-5 h-5" data-id="element-491" />} data-id="element-490">
          View AI Review
        </PrimaryButton>
        <SecondaryButton onClick={() => {}} icon={<PlayIcon className="w-5 h-5 fill-current" data-id="element-493" />} data-id="element-492">
          Play Recording
        </SecondaryButton>
      </div>
    </div>;
}